import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, ChevronRight, Globe, Phone } from "lucide-react";
import { toast } from "sonner";
import { ALL_COUNTRIES } from "../data/payoutCountries";

const AuthPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountType = searchParams.get("type"); // "fan" | "artist" | "corporate"
  const isCorporate = accountType === "corporate";
  const [mode, setMode] = useState(accountType ? "signup" : "signin");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone_number: "",
    company_name: "", business_address: "", business_website: "",
    country: "CA"
  });
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  // 2FA state
  const [twoFAChallenge, setTwoFAChallenge] = useState(null); // {challenge_id, sent_to}
  const [twoFACode, setTwoFACode] = useState("");
  const [newRecoveryCode, setNewRecoveryCode] = useState("");
  const [postRegisterNav, setPostRegisterNav] = useState("");
  const [savedAck, setSavedAck] = useState(false);
  // ---- Bot-Shield state (iteration 75) ----
  // Fetched on signup mount from /api/auth/bot-shield-challenge.
  // Honeypot field name is dynamic — a rogue frontend fork can't guess it.
  const [botShield, setBotShield] = useState({
    challenge_token: null,
    honeypot_field: "hp_verify_token",
    window_secs: 90,
    min_human_ms: 800,
  });
  const [honeypotValue, setHoneypotValue] = useState("");
  const formStartedAtMsRef = useRef(null);

  // Fetch bot-shield challenge whenever we enter signup mode.
  // Refresh every 60s so the challenge doesn't age out during a long fill.
  useEffect(() => {
    if (mode !== "signup") return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/auth/bot-shield-challenge`, {
          params: { form_id: "signup" },
        });
        if (cancelled) return;
        setBotShield({
          challenge_token: res.data?.challenge_token || null,
          honeypot_field: res.data?.honeypot_field || "hp_verify_token",
          window_secs: res.data?.window_secs || 90,
          min_human_ms: res.data?.min_human_ms || 800,
        });
      } catch (e) {
        // Silent — signup still works; backend may allow if shield check errors.
      }
    };
    load();
    // Stamp form start on first mount of signup.
    if (formStartedAtMsRef.current == null) {
      formStartedAtMsRef.current = Date.now();
    }
    // Refresh challenge every 60s while user fills the form.
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "signup") {
        if (!form.name.trim()) { setError("Name is required"); setLoading(false); return; }
        if (form.password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
        const phoneDigits = (form.phone_number || "").replace(/\D/g, "");
        if (phoneDigits.length < 10 || phoneDigits.length > 15) {
          setError("A valid phone number is required (10–15 digits)");
          setLoading(false);
          return;
        }
        
        let res;
        if (isCorporate) {
          if (!form.company_name.trim()) { setError("Company name is required"); setLoading(false); return; }
          if (!form.business_address.trim()) { setError("Business address is required"); setLoading(false); return; }
          res = await axios.post(`${API}/corporate/register`, {
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password,
            phone_number: form.phone_number.trim(),
            company_name: form.company_name.trim(),
            business_address: form.business_address.trim(),
            business_website: form.business_website.trim() || undefined
          });
        } else {
          const refCode = searchParams.get("ref") || localStorage.getItem("pending_referral") || undefined;
          res = await axios.post(`${API}/auth/register`, {
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password,
            phone_number: form.phone_number.trim(),
            referral_code: refCode,
            country: form.country || "CA",
            // Bot-shield fields (iteration 75) — honeypot MUST stay empty,
            // form_started_at_ms proves >800ms human dwell, challenge_token
            // proves the browser fetched the challenge on this window.
            [botShield.honeypot_field || "hp_verify_token"]: honeypotValue,
            form_started_at_ms: formStartedAtMsRef.current || Date.now(),
            challenge_token: botShield.challenge_token,
          });
        }
        localStorage.setItem("session_token", res.data.session_token);
        await onLogin(res.data.session_token);
        // Apply referral code if present (fallback for pre-signup flow, e.g., corporate)
        const refCode = searchParams.get("ref") || localStorage.getItem("pending_referral");
        if (refCode && !isCorporate) {
          try {
            await axios.post(`${API}/referrals/apply`, { code: refCode }, {
              headers: { Authorization: `Bearer ${res.data.session_token}` }
            });
            toast.success(`Welcome to StardomFans.com! Referral applied 🎁`);
          } catch {
            toast.success("Welcome to StardomFans.com!");
          }
          localStorage.removeItem("pending_referral");
        } else if (isCorporate) {
          if (res.data.ai_flag_status === "flagged") {
            toast.error("Welcome — your store is pending admin review.");
          } else {
            toast.success("Corporate account created. Welcome to StardomFans.com!");
          }
        } else {
          toast.success("Welcome to StardomFans.com!");
        }
        // CRITICAL: show the Secret Account ID Code ONCE before navigating.
        // Every new signup lands in /id-vault first — the KYC gate blocks
        // every other page until ID + selfie are approved. Corporate signups
        // still go to /corporate-dashboard (they don't purchase Tokens).
        const code = res.data?.recovery_code;
        const nextAfterSignup = isCorporate ? "/corporate-dashboard" : (res.data?.next || "/id-vault");
        if (code) {
          setNewRecoveryCode(code);
          setPostRegisterNav(nextAfterSignup);
        } else {
          navigate(nextAfterSignup);
        }
      } else {
        const res = await axios.post(`${API}/auth/login`, {
          email: form.email.trim(),
          password: form.password
        });
        if (res.data?.needs_2fa) {
          setTwoFAChallenge({ challenge_id: res.data.challenge_id, sent_to: res.data.sent_to });
          toast.success(`A code was emailed to ${res.data.sent_to}`);
        } else {
          localStorage.setItem("session_token", res.data.session_token);
          // /auth/login now stamps is_admin — pass it directly so the
          // admin gate turns on immediately without a follow-up /auth/me.
          const loggedUser = await onLogin(res.data.session_token, {
            user: { ...res.data.user, kyc_status: res.data.kyc_status },
            is_admin: !!res.data.is_admin,
          });
          toast.success("Welcome back!");
          // KYC gate — non-admin users with unapproved KYC always land in
          // /id-vault first, regardless of the ?next query string.
          const kyc = res.data.kyc_status || loggedUser?.kyc_status || "not_started";
          const nextParam = searchParams.get("next");
          if (!res.data.is_admin && kyc !== "approved") {
            navigate("/id-vault");
          } else if (nextParam && nextParam.startsWith("/")) {
            navigate(nextParam);
          } else {
            navigate(loggedUser?.role === "corporate" ? "/corporate-dashboard" : "/dashboard");
          }
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      else setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFA = async (e) => {
    e?.preventDefault();
    setError("");
    if (!twoFACode || twoFACode.length !== 6) { setError("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify-login`, {
        challenge_id: twoFAChallenge.challenge_id,
        code: twoFACode
      });
      localStorage.setItem("session_token", res.data.session_token);
      const loggedUser = await onLogin(res.data.session_token);
      toast.success("Welcome back!");
      navigate(loggedUser?.role === "corporate" ? "/corporate-dashboard" : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Wrong code");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError("Please enter your email address"); return; }
    setForgotLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: form.email.trim(),
        origin_url: window.location.origin
      });
      setForgotSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] flex">
      {/* Secret Account ID Code modal — shown ONCE after signup */}
      {newRecoveryCode && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="recovery-code-modal">
          <div className="max-w-lg w-full bg-[#0a0e1a] border-2 border-[#EC008C] rounded-2xl p-6 sm:p-8 shadow-[0_0_60px_rgba(255,51,0,0.4)]">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-[#EC008C]/15 border border-[#EC008C] flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🔐</span>
              </div>
              <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Your Secret Account ID Code</h2>
              <p className="text-[#8A92A0] text-sm">
                Save this code somewhere safe. <span className="text-[#EC008C] font-semibold">It's the ONLY way to recover your account</span> if you ever lose access. We don't store this in plain text — it cannot be retrieved later.
              </p>
            </div>
            <div className="bg-[#050810] border border-[#EC008C]/40 rounded-xl p-5 mb-5 text-center">
              <p className="font-mono text-2xl sm:text-3xl font-bold tracking-widest text-[#EC008C] break-all select-all" data-testid="recovery-code-value">
                {newRecoveryCode}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mb-5">
              <button
                onClick={() => { navigator.clipboard.writeText(newRecoveryCode); toast.success("Copied"); }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2030] hover:bg-[#1A2030]/70 text-white text-sm font-semibold"
                data-testid="copy-recovery-code-btn"
              >📋 Copy Code</button>
              <button
                onClick={() => {
                  const blob = new Blob([`StardomFans Secret Account ID Code\n\nEmail: ${form.email.trim()}\nCode: ${newRecoveryCode}\nIssued: ${new Date().toISOString()}\n\nKeep this code safe. It is the only way to recover your account if you lose access. Do not share it with anyone.`], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `StardomFans-Recovery-Code.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Downloaded");
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2030] hover:bg-[#1A2030]/70 text-white text-sm font-semibold"
                data-testid="download-recovery-code-btn"
              >⬇️ Download .txt</button>
            </div>
            <label className="flex items-start gap-3 mb-5 cursor-pointer" data-testid="recovery-ack-label">
              <input type="checkbox" checked={savedAck} onChange={(e) => setSavedAck(e.target.checked)} className="mt-1 w-5 h-5 accent-[#EC008C]" data-testid="recovery-ack-checkbox" />
              <span className="text-[#8A92A0] text-sm">I have saved my Secret Account ID Code in a safe place. I understand it cannot be retrieved later.</span>
            </label>
            <button
              onClick={() => {
                if (!savedAck) { toast.error("Please confirm you've saved your code"); return; }
                setNewRecoveryCode("");
                navigate(postRegisterNav || "/id-vault");
              }}
              disabled={!savedAck}
              className={`w-full py-3 rounded-full font-bold text-base transition-all ${savedAck ? "bg-[#EC008C] text-black hover:bg-[#FF3399] hover:scale-[1.02]" : "bg-[#1A2030] text-[#6A7280] cursor-not-allowed"}`}
              data-testid="recovery-continue-btn"
            >Continue to Dashboard</button>
          </div>
        </div>
      )}

      {/* Left - Image Panel */}
      <div className="hidden lg:flex lg:w-1/2 auth-brand-panel">
        <img
          src="/logo.png"
          alt="StardomFans logo"
          className="auth-brand-art"
          data-testid="auth-brand-image"
        />
        <div className="auth-brand-copy">
          <h2 className="font-['Unbounded'] text-4xl font-bold text-white mb-4">
            Your Stage Awaits
          </h2>
          <p className="text-[#8A92A0] text-lg max-w-md">
            Join the next generation of artists and fans. Upload music, sell tickets, connect with your fandom — all in one place.
          </p>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Back */}
          <button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-2 text-[#8A92A0] hover:text-white mb-8 transition-colors text-sm"
            data-testid="auth-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to StardomFans.com
          </button>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 rounded-xl object-cover" />
          </div>

          {/* Heading */}
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2" data-testid="auth-title">
            {forgotMode ? "Reset your password" : mode === "signup" ? (accountType === "artist" ? "Create your Artist account" : accountType === "fan" ? "Create your Fan account" : "Create your account") : "Welcome back"}
          </h1>
          <p className="text-[#8A92A0] mb-8">
            {forgotMode
              ? "Enter your email and we'll send you a reset link"
              : mode === "signup" 
                ? (accountType === "artist" ? "Upload music, sell tickets, build your empire. You keep 90%." : accountType === "fan" ? "Discover artists, collect drops, join the global party." : "Sign up to start your journey")
                : "Sign in to continue to your dashboard"}
          </p>

          {/* Forgot Password Sent State */}
          {forgotMode && forgotSent ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#EC008C]" />
              </div>
              <h2 className="text-white font-semibold text-lg mb-2">Check your email</h2>
              <p className="text-[#8A92A0] text-sm mb-6">If an account exists with that email, you'll receive a password reset link.</p>
              <button onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }} className="text-[#EC008C] font-medium hover:underline text-sm" data-testid="back-to-signin-btn">
                Back to Sign In
              </button>
            </div>
          ) : forgotMode ? (
            <>
              {/* Error */}
              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-[#D14A85]/10 border border-[#D14A85]/20 text-[#D14A85] text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address"
                    required
                    className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-[#EC008C] text-[#000000] font-semibold rounded-xl py-3 text-sm hover:bg-[#FF3399] transition-colors disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
              <p className="text-center mt-6 text-[#8A92A0] text-sm">
                <button onClick={() => { setForgotMode(false); setError(""); }} className="text-[#EC008C] font-medium hover:underline" data-testid="back-to-signin-from-forgot">Back to Sign In</button>
              </p>
            </>
          ) : (
          <>
          {/* Error */}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-[#D14A85]/10 border border-[#D14A85]/20 text-[#D14A85] text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* Form (or 2FA challenge) */}
          {twoFAChallenge ? (
            <form onSubmit={verifyTwoFA} className="space-y-4" data-testid="two-fa-challenge">
              <div className="bg-[#EC008C]/5 border border-[#EC008C]/30 rounded-lg p-3 text-sm text-[#8A92A0]">
                Two-factor authentication is enabled on this account. We sent a 6-digit code to <span className="text-white font-semibold">{twoFAChallenge.sent_to}</span>.
              </div>
              <input
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0,6))}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-3 text-center text-2xl font-mono tracking-widest text-white"
                data-testid="two-fa-code-input"
              />
              <button
                type="submit"
                disabled={loading || twoFACode.length !== 6}
                className="w-full px-4 py-3 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] disabled:opacity-60 text-sm"
                data-testid="two-fa-verify-btn"
              >
                {loading ? "Verifying..." : "Verify & Sign In"}
              </button>
              <button
                type="button"
                onClick={() => { setTwoFAChallenge(null); setTwoFACode(""); setError(""); }}
                className="w-full text-sm text-[#8A92A0] hover:text-white"
                data-testid="two-fa-cancel-btn"
              >
                ← Back to sign in
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot — invisible to humans, irresistible to bots. Field
                name is dynamic (server-supplied) so a rogue frontend fork
                can't hardcode a bypass. Wrapped in absolute-positioned
                off-screen container + aria-hidden + tabIndex=-1 so screen
                readers + keyboard users skip it. */}
            {mode === "signup" && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "-9999px",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              >
                <label htmlFor={botShield.honeypot_field || "hp_verify_token"}>
                  Leave this field empty
                </label>
                <input
                  type="text"
                  id={botShield.honeypot_field || "hp_verify_token"}
                  name={botShield.honeypot_field || "hp_verify_token"}
                  value={honeypotValue}
                  onChange={(e) => setHoneypotValue(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  data-testid="bot-shield-honeypot"
                />
              </div>
            )}
            {mode === "signup" && (
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={isCorporate ? "Contact name (yours)" : "Full name"}
                  required
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
                  data-testid="auth-name-input"
                />
              </div>
            )}

            {mode === "signup" && (
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                <input
                  type="tel"
                  value={form.phone_number}
                  onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  placeholder="Phone number (10–15 digits, incl. country code)"
                  required
                  autoComplete="tel"
                  inputMode="tel"
                  minLength={10}
                  maxLength={20}
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
                  data-testid="auth-phone-input"
                />
              </div>
            )}

            {mode === "signup" && !isCorporate && (
              <div className="relative">
                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280] pointer-events-none" />
                <select
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  required
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white focus:outline-none focus:border-[#EC008C]/50 transition-colors appearance-none"
                  data-testid="auth-country-select"
                  aria-label="Your country (used to set up your payout region if you sell)"
                >
                  {ALL_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280] rotate-90 pointer-events-none" />
                <p className="text-xs text-[#6A7280] mt-1.5 px-1">
                  Sets your payout region if you sell. You can change this later.
                </p>
              </div>
            )}

            {mode === "signup" && isCorporate && (
              <>
                <input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Company name"
                  required
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl px-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                  data-testid="auth-company-input"
                />
                <input
                  value={form.business_address}
                  onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                  placeholder="Business address"
                  required
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl px-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                  data-testid="auth-address-input"
                />
                <input
                  type="url"
                  value={form.business_website}
                  onChange={(e) => setForm({ ...form, business_website: e.target.value })}
                  placeholder="Business website (optional)"
                  className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl px-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                  data-testid="auth-website-input"
                />
              </>
            )}

            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                required
                className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
                data-testid="auth-email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                required
                minLength={6}
                className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-11 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6A7280] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EC008C] text-[#000000] font-semibold rounded-xl py-3 text-sm hover:bg-[#FF3399] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="auth-submit-btn"
            >
              {loading ? "Please wait..." : (
                <>
                  {mode === "signup" ? "Create Account" : "Sign In"}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
          )}

          {/* Forgot Password Link — goes to in-house Secret Account ID Code recovery */}
          {mode === "signin" && (
            <p className="text-right mt-2">
              <Link to="/auth/recover" className="text-[#6A7280] text-xs hover:text-[#EC008C] transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </p>
          )}

          {/* Toggle */}
          <p className="text-center mt-6 text-[#8A92A0] text-sm">
            {mode === "signup" ? (
              <>Already have an account? <button onClick={() => { setMode("signin"); setError(""); }} className="text-[#EC008C] font-medium hover:underline" data-testid="switch-to-signin">Sign in</button></>
            ) : (
              <>Don't have an account? <button onClick={() => { setMode("signup"); setError(""); }} className="text-[#EC008C] font-medium hover:underline" data-testid="switch-to-signup">Sign up</button></>
            )}
          </p>
          </>
          )}

          {/* Footer */}
          <p className="text-center mt-8 text-[#4A5260] text-xs">
            By continuing, you agree to the StardomFans.com <Link to="/terms" className="text-[#6A7280] hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#6A7280] hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
